import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Skeleton,
  Divider,
} from '@mui/material';
import { format } from 'date-fns';
import apiService from '../../../services/api';

interface ActivityEntry {
  id: string;
  created_at: string;
  action: string;
  details: string;
  ip_address: string;
  admin_email: string;
  admin_name: string;
  log_type: 'action' | 'permission';
}

const ACTION_CHIP_CONFIG: Record<string, { color: 'success' | 'error' | 'info' | 'warning' | 'default'; label?: string }> = {
  login: { color: 'success' },
  logout: { color: 'default' },
  created: { color: 'info', label: 'Created' },
  updated: { color: 'warning', label: 'Updated' },
  revoked: { color: 'error', label: 'Revoked' },
  view_dashboard: { color: 'default', label: 'View' },
  export_data: { color: 'info', label: 'Export' },
};

const AdminActivityLog: React.FC = () => {
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);

  const loadActivityLog = async (pageNum: number, limit: number) => {
    try {
      setLoading(true);
      const data = await apiService.getAdminActivityLog(pageNum + 1, limit);
      setEntries(data.entries || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to load activity log:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadActivityLog(page, rowsPerPage);
  }, [page, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getActionChip = (action: string) => {
    const config = ACTION_CHIP_CONFIG[action] || { color: 'default' as const };
    const label = config.label || action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    return <Chip size="small" label={label} color={config.color} variant="outlined" />;
  };

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Admin Activity Log
        </Typography>
        <Typography variant="body2" color="text.secondary">
          All admin actions including logins, permission changes, and user management
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />

      {loading && entries.length === 0 ? (
        <Box>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rectangular" height={40} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Admin</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>IP Address</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No activity logged yet</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((entry) => (
                    <TableRow key={`${entry.log_type}-${entry.id}`} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2">
                          {format(new Date(entry.created_at), 'MMM dd, yyyy')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(entry.created_at), 'h:mm:ss a')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {entry.admin_name || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {entry.admin_email}
                        </Typography>
                      </TableCell>
                      <TableCell>{getActionChip(entry.action)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {entry.details || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {entry.ip_address || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </>
      )}
    </Paper>
  );
};

export default AdminActivityLog;
